import type { SubmittedCampaignRequest } from './types';

interface ConfirmationProps {
  submitted: SubmittedCampaignRequest;
  onReset: () => void;
}

export function CampaignConfirmation({ submitted, onReset }: ConfirmationProps) {
  const total = submitted.items.reduce((sum, item) => sum + item.places, 0);

  return (
    <div className="backdrop-blur-sm bg-white/5 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-white/10">
      <div className="gl-fade-up mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold mb-2 text-white">Request received</h2>
        <p className="text-white/70 text-sm sm:text-base">
          We'll email <span className="text-white font-medium">{submitted.email}</span> once
          the organizer reviews your request.
        </p>
      </div>

      <div
        className="gl-fade-up rounded-lg border border-white/10 bg-black/30 divide-y divide-white/10 text-sm sm:text-base"
        style={{ animationDelay: '100ms' }}
      >
        <DetailRow label="Name" value={`${submitted.firstName} ${submitted.lastName}`.trim()} />
        <DetailRow label="Email" value={submitted.email} />
        <div className="px-3 sm:px-4 py-3">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Events</div>
          <ul className="space-y-1.5">
            {submitted.items.map((item) => (
              <li key={item.eventId} className="flex items-center justify-between gap-3">
                <span className="text-white/80 truncate">{item.eventName}</span>
                <span className="text-white shrink-0">
                  {item.places} {item.places === 1 ? 'spot' : 'spots'}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/10">
            <span className="text-white/80">Total</span>
            <span className="text-white font-semibold">
              {total} {total === 1 ? 'spot' : 'spots'}
            </span>
          </div>
        </div>
        {submitted.requestedTrough && (
          <DetailRow label="Referred by" value={submitted.requestedTrough} />
        )}
        {submitted.message && <DetailRow label="Notes" value={submitted.message} multiline />}
      </div>

      <div className="gl-fade-up flex justify-center mt-6 sm:mt-8" style={{ animationDelay: '200ms' }}>
        <button
          type="button"
          onClick={onReset}
          className="text-xs sm:text-sm text-white/60 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white transition-colors"
        >
          Submit another request
        </button>
      </div>
    </div>
  );
}

function DetailRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className="flex gap-3 px-3 sm:px-4 py-2.5">
      <div className="text-xs uppercase tracking-wider text-white/50 w-24 shrink-0 pt-0.5">{label}</div>
      <div className={`text-white/90 ${multiline ? 'whitespace-pre-wrap break-words' : 'truncate'}`}>
        {value}
      </div>
    </div>
  );
}
